import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { blogPosts } from '../../data/blog-posts';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import BlogModal from './BlogModal';

// Fonction pour parser les dates françaises
function parseFrenchDate(dateString: string): Date {
  const months: { [key: string]: number } = {
    'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
    'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
    'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
  };

  const parts = dateString.toLowerCase().split(' ');
  if (parts.length === 3) {
    const day = parseInt(parts[0]);
    const month = months[parts[1]];
    const year = parseInt(parts[2]);

    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }

  return new Date(dateString);
}

function generateSlug(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/['']/g, '')
    .replace(/[?]/g, '')
    .replace(/[.]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/--+/g, '-');
}

const BlogCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const itemsPerPage = 6; // Nombre d'articles visibles à la fois (réduit de 50%)

  // Trier et prendre les 10 derniers articles
  const sortedPosts = [...blogPosts]
    .sort((a, b) => {
      const dateA = parseFrenchDate(a.date);
      const dateB = parseFrenchDate(b.date);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 10);

  // Auto-play carousel
  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (sortedPosts.length - itemsPerPage + 1));
    }, 5000); // Défilement toutes les 5 secondes

    return () => clearInterval(interval);
  }, [autoPlay, sortedPosts.length]);

  const handlePrevious = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) =>
      prev === 0 ? sortedPosts.length - itemsPerPage : prev - 1
    );
  };

  const handleNext = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) =>
      (prev + 1) % (sortedPosts.length - itemsPerPage + 1)
    );
  };

  const visiblePosts = sortedPosts.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <>
      <div className="relative w-full pt-8 pb-12" style={{ isolation: 'isolate' }}>
        <div className="container mx-auto px-4">
          <div className="relative">
            {/* Bouton Précédent */}
            <button
              onClick={handlePrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all"
              style={{ left: '-20px' }}
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
            </button>

            {/* Carrousel d'articles */}
            <div className="overflow-hidden" style={{ isolation: 'isolate', zIndex: 1 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
                style={{ position: 'relative', zIndex: 1 }}
              >
                {visiblePosts.map((post) => {
                  const slug = generateSlug(post.id);
                  const postDate = parseFrenchDate(post.date);
                  const formattedDate = postDate.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  });

                  return (
                    <div
                      key={post.id}
                      onClick={() => setSelectedPost(post)}
                      className="group cursor-pointer"
                    >
                      <motion.article
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.2 }}
                        className="rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                        style={{
                          background: 'rgba(128, 128, 128, 0.12)',
                          backdropFilter: 'blur(10px) saturate(150%)',
                          WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                          border: '1px solid rgba(200, 200, 200, 0.3)',
                        }}
                      >
                        {/* Image */}
                        {post.image && (
                          <div className="relative h-24 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600" style={{ isolation: 'isolate', contain: 'layout' }}>
                            <img
                              src={post.image}
                              alt={post.title}
                              className="w-full h-full object-cover opacity-80"
                              style={{ position: 'absolute', inset: 0 }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        {/* Contenu */}
                        <div className="p-4">
                          <p className="text-xs text-gray-600 mb-2 font-medium drop-shadow-sm">
                            {formattedDate}
                          </p>
                          <h3 className="text-sm font-bold text-gray-700 line-clamp-2 group-hover:text-blue-600 transition-colors leading-snug drop-shadow-sm">
                            {post.title}
                          </h3>
                        </div>
                      </motion.article>
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>

            {/* Bouton Suivant */}
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all"
              style={{ right: '-20px' }}
            >
              <ChevronRightIcon className="h-6 w-6 text-gray-600" />
            </button>

          </div>
        </div>
      </div>

      {/* Blog Modal - En dehors du conteneur principal */}
      <BlogModal
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
      />
    </>
  );
};

export default BlogCarousel;